// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's selection on the plan (frontend-u0o.9): what lies
 * under a click (in the order Tab cycles them), Shift adding to the
 * selection, and the box — dragged left to right a *window* (only what lies
 * wholly inside), right to left a *crossing* (whatever it touches), as in
 * Revit and AutoCAD. The box arrives as four world corners (the plan may be
 * turned on screen), so every test runs against a convex quad. Pure: no
 * React, no DOM.
 */

import type { SolidOpening, SolidSpace, SolidWall } from '../api/types';
import type { GeometriModel } from './geometri';
import type { XY } from './planWalls';
import { stairFootprint } from './stairs';

export type BoxMode = 'window' | 'crossing';

/** L→R is a window, R→L a crossing (screen x of the drag's start and end). */
export function boxMode(startX: number, endX: number): BoxMode {
  return endX >= startX ? 'window' : 'crossing';
}

const cross = (o: XY, a: XY, b: XY) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/** Whether `p` is inside the convex polygon `quad` (either winding), edges included. */
export function inConvex(quad: readonly XY[], p: XY): boolean {
  let sign = 0;
  for (let i = 0; i < quad.length; i += 1) {
    const c = cross(quad[i], quad[(i + 1) % quad.length], p);
    if (Math.abs(c) < 1e-12) continue;
    const s = Math.sign(c);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

function segmentsIntersect(p1: XY, p2: XY, p3: XY, p4: XY): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  const on = (a: XY, b: XY, c: XY, d: number) =>
    Math.abs(d) < 1e-12 && Math.min(a[0], b[0]) - 1e-12 <= c[0] && c[0] <= Math.max(a[0], b[0]) + 1e-12 && Math.min(a[1], b[1]) - 1e-12 <= c[1] && c[1] <= Math.max(a[1], b[1]) + 1e-12;
  return on(p3, p4, p1, d1) || on(p3, p4, p2, d2) || on(p1, p2, p3, d3) || on(p1, p2, p4, d4);
}

/** A polyline (or ring, `closed`) against the quad: wholly inside, or touching it at all. */
export function polyIn(quad: readonly XY[], pts: readonly XY[], mode: BoxMode, closed: boolean): boolean {
  if (pts.length === 0) return false;
  const inside = pts.map((p) => inConvex(quad, p));
  if (mode === 'window') return inside.every(Boolean);
  if (inside.some(Boolean)) return true;
  const n = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < n; i += 1) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    for (let j = 0; j < quad.length; j += 1) if (segmentsIntersect(a, b, quad[j], quad[(j + 1) % quad.length])) return true;
  }
  return false;
}

/** Even-odd point in ring. */
export function pointInRing(ring: readonly XY[], p: XY): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function openingEnds(o: SolidOpening, host: SolidWall | undefined): XY[] | null {
  if (o.start && o.end) return [o.start, o.end];
  if (!host) return null;
  const L = Math.hypot(host.end[0] - host.start[0], host.end[1] - host.start[1]);
  if (!(L > 0)) return null;
  const at = (s: number): XY => [host.start[0] + ((host.end[0] - host.start[0]) * s) / L, host.start[1] + ((host.end[1] - host.start[1]) * s) / L];
  return [at(o.s_start), at(o.s_end)];
}

/** Only rows on storey `storeyId` (null = every storey); an opening goes by its host's. */
function onStorey<T extends { storey_id: number | null }>(rows: readonly T[] | null, storeyId: number | null): T[] {
  return (rows ?? []).filter((r) => storeyId === null || r.storey_id === storeyId);
}

/**
 * The elements the box selects on storey `storeyId`: walls by their axis,
 * openings by their span on the host's axis, spaces by their outline. A
 * window takes what lies wholly inside; a crossing whatever it touches —
 * but spaces only when it touched no wall or opening (a crossing over a
 * wall would otherwise always take the rooms on both sides), and a space
 * holding the whole box (drawn inside a room) only when nothing else.
 */
export function boxSelect(model: GeometriModel, quad: readonly XY[], mode: BoxMode, storeyId: number | null): string[] {
  const walls = onStorey(model.walls, storeyId);
  const hosts = new Map((model.walls ?? []).map((w) => [w.guid, w]));
  const out: string[] = [];
  for (const w of walls) if (polyIn(quad, [w.start, w.end], mode, false)) out.push(w.guid);
  for (const o of model.openings ?? []) {
    const host = hosts.get(o.wall_guid);
    if (storeyId !== null && (o.storey_id ?? host?.storey_id ?? null) !== storeyId) continue;
    const ends = openingEnds(o, host);
    if (ends && polyIn(quad, ends, mode, false)) out.push(o.guid);
  }
  // Stairs (S8): a window takes one wholly inside; a crossing one it touches or lies in.
  for (const t of onStorey(model.stairs ?? null, storeyId)) {
    const rings = stairFootprint(t);
    if (rings.length === 0) continue;
    const hit =
      mode === 'window'
        ? rings.every((r) => polyIn(quad, r, mode, true))
        : rings.some((r) => polyIn(quad, r, mode, true) || (quad.length > 0 && quad.every((p) => pointInRing(r, p))));
    if (hit) out.push(t.guid);
  }
  // A crossing over a wall touches the rooms on both sides of it: those
  // count only when the box caught nothing else (or held the box whole).
  const touched: string[] = [];
  const containing: string[] = [];
  for (const s of onStorey(model.spaces, storeyId)) {
    const ring = s.polygon.outer;
    if (polyIn(quad, ring, mode, true)) (mode === 'window' ? out : touched).push(s.guid);
    else if (mode === 'crossing' && quad.length > 0 && quad.every((p) => pointInRing(ring, p))) containing.push(s.guid);
  }
  if (out.length > 0) return out;
  if (touched.length > 0) return touched;
  return containing;
}

function distToSegment(a: XY, b: XY, p: XY): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const L2 = dx * dx + dy * dy;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0;
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t));
}

const ringArea = (ring: readonly XY[]) => {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) sum += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  return Math.abs(sum / 2);
};

/**
 * Everything under plan point `p` on storey `storeyId`, in the order a
 * click picks and Tab then cycles: openings (drawn over their wall), walls
 * nearest first (within half their thickness or `tol` metres), then the
 * spaces holding `p`, smallest first, then the slabs holding it.
 */
export function elementsAt(model: GeometriModel, p: XY, tol: number, storeyId: number | null): string[] {
  const walls = onStorey(model.walls, storeyId);
  const hosts = new Map((model.walls ?? []).map((w) => [w.guid, w]));
  const hits: { guid: string; d: number; rank: number }[] = [];
  for (const o of model.openings ?? []) {
    const host = hosts.get(o.wall_guid);
    if (!host || (storeyId !== null && host.storey_id !== storeyId)) continue;
    const ends = openingEnds(o, host);
    if (!ends) continue;
    const d = distToSegment(ends[0], ends[1], p);
    if (d <= Math.max((host.thickness ?? 0) / 2, tol)) hits.push({ guid: o.guid, d, rank: 0 });
  }
  for (const w of walls) {
    const d = distToSegment(w.start, w.end, p);
    if (d <= Math.max((w.thickness ?? 0) / 2, tol)) hits.push({ guid: w.guid, d, rank: 1 });
  }
  // A stair stands in a room: it comes before the room (S8).
  for (const t of onStorey(model.stairs ?? null, storeyId)) if (stairFootprint(t).some((r) => pointInRing(r, p))) hits.push({ guid: t.guid, d: 0, rank: 2 });
  const spaces: SolidSpace[] = onStorey(model.spaces, storeyId).filter((s) => pointInRing(s.polygon.outer, p));
  spaces.sort((a, b) => ringArea(a.polygon.outer) - ringArea(b.polygon.outer));
  hits.sort((a, b) => a.rank - b.rank || a.d - b.d);
  // The slabs under it last (S7): a click takes the room, Tab reaches the floor.
  const slabs = (model.slabs ?? [])
    .filter((s) => (storeyId === null || s.storey_id === storeyId) && pointInRing(s.polygon.outer, p) && !s.polygon.holes.some((h) => pointInRing(h, p)))
    .sort((a, b) => ringArea(a.polygon.outer) - ringArea(b.polygon.outer));
  return [...hits.map((h) => h.guid), ...spaces.map((s) => s.guid), ...slabs.map((s) => s.guid)];
}

/** The next candidate after `current` (Tab), wrapping; the first when `current` is not among them. */
export function cycle(candidates: readonly string[], current: string | null, delta = 1): string | null {
  if (candidates.length === 0) return null;
  const at = current === null ? -1 : candidates.indexOf(current);
  if (at < 0) return candidates[0];
  return candidates[(at + delta + candidates.length) % candidates.length];
}

/**
 * The selection after a click on `hit` (null = empty space): a plain click
 * selects it alone (or clears), Shift toggles it in or out of the
 * selection and never clears.
 */
export function clickSelect(current: readonly string[], hit: string | null, additive: boolean): string[] {
  if (!additive) return hit === null ? [] : [hit];
  if (hit === null) return [...current];
  return current.includes(hit) ? current.filter((g) => g !== hit) : [...current, hit];
}

/** A box's result merged into the selection: replaces it, or with Shift adds to it. */
export function boxMerge(current: readonly string[], picked: readonly string[], additive: boolean): string[] {
  if (!additive) return [...picked];
  const out = [...current];
  for (const g of picked) if (!out.includes(g)) out.push(g);
  return out;
}
